import React, { useMemo, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { DqMarker } from "./psPrimitives";
import {
  STRUCT, SGROUPS, SLOT, CF, PANELS, COLORS, EMF, CATEGORIES, POS_OPTS,
  clone, mkSecs, structGroupFor, useVisitTypes, saveVisitType, removeVisitType,
} from "@/lib/visitTypesData";

const IND = "#5856D6", AMBER = "#9A5A00";

export function Switch({ checked, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} disabled={disabled} onClick={() => !disabled && onChange(!checked)}
      className="relative inline-flex flex-none w-[38px] h-[22px] rounded-full transition disabled:opacity-40"
      style={{ background: checked ? IND : "#D6D6DB" }}>
      <span className="absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white transition-all" style={{ left: checked ? 18 : 2, boxShadow: "0 1px 2px rgba(0,0,0,.25)" }} />
    </button>
  );
}
function SwitchRow({ label, sub, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-t border-[#EDEDF0] first:border-t-0">
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-[var(--ink)]">{label}</div>
        {sub ? <div className="text-[11px] text-[var(--gray2)] mt-0.5">{sub}</div> : null}
      </div>
      <Switch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="block text-[11px] font-semibold text-[var(--gray2)] mb-1">{label}</span>{children}</label>;
}
const SEL = "w-full rounded-[9px] border border-black/[.14] bg-white px-2.5 py-2 text-[12.5px] text-[var(--ink)] outline-none focus:border-[#5856D6]";
function Sel({ value, onChange, opts }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className={SEL}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
}
function Inp({ value, onChange, type, placeholder }) {
  return <input value={value ?? ""} type={type} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={SEL} />;
}
function Fsec({ title, children }) {
  return <section className="rounded-[14px] border border-[#E9E9EC] bg-white p-4 mb-3"><div className="text-[12px] font-bold text-[var(--ink)] mb-2.5">{title}</div>{children}</section>;
}

/* --------- shared section rows (editor + provider overrides) --------- */
export function SectionRows({ list, editable, showMap, onChange }) {
  const set = (next) => onChange(next);
  const patch = (i, k, v) => { const n = clone(list); n[i][k] = v; set(n); };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const n = clone(list); [n[i], n[j]] = [n[j], n[i]]; set(n); };
  return (
    <div className="rounded-[12px] border border-[#E9E9EC] overflow-hidden" data-testid="vt-sections">
      <div className="grid grid-cols-[22px_44px_1fr_34px_200px_54px] gap-2 px-3 py-1.5 text-[10px] font-bold uppercase text-[var(--gray2)]" style={{ letterSpacing: ".04em", background: "#F5F5F6" }}>
        <span /><span /><span>Section</span><span className="text-center">#</span><span>Copy forward from prior note</span><span className="text-center">In note</span>
      </div>
      {list.map((x, i) => (
        <div key={i} className="grid grid-cols-[22px_44px_1fr_34px_200px_54px] gap-2 px-3 py-2 items-center text-[12.5px]" style={{ borderTop: i ? "1px solid #EDEDF0" : undefined, opacity: x.on ? 1 : 0.55 }}>
          <span className="text-[var(--gray3)] cursor-grab select-none" aria-hidden="true">⋮⋮</span>
          <span className="flex gap-0.5">
            {editable ? <>
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="w-5 h-5 rounded text-[var(--gray2)] hover:bg-black/[.05] disabled:opacity-30" aria-label="Move up">▲</button>
              <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} className="w-5 h-5 rounded text-[var(--gray2)] hover:bg-black/[.05] disabled:opacity-30" aria-label="Move down">▼</button>
            </> : null}
          </span>
          <span className="min-w-0">
            {editable
              ? <input value={x.label} onChange={(e) => patch(i, "label", e.target.value)} className="w-full rounded-[7px] border border-black/[.12] bg-white px-2 py-1 text-[12.5px] text-[var(--ink)] outline-none focus:border-[#5856D6]" aria-label="Section label" />
              : <span className="font-medium text-[var(--ink)]">{x.label}</span>}
            {x.label !== x.orig ? <span className="ml-2 text-[10.5px] italic text-[var(--gray3)]">was {x.orig}</span> : null}
            {showMap ? <span className="block text-[10.5px] mt-0.5" style={{ color: IND }}>→ {SLOT[x.slot] || x.slot}</span> : null}
          </span>
          <span className="text-center text-[var(--gray2)]">{i + 1}</span>
          <span>
            {x.slot === "narrative" ? <span className="text-[11.5px] text-[var(--gray3)]">Not applicable</span>
              : editable ? <select value={x.cf} onChange={(e) => patch(i, "cf", e.target.value)} className="w-full rounded-[7px] border border-black/[.12] bg-white px-1.5 py-1 text-[11.5px]">{Object.entries(CF).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                : <span className="text-[11.5px] text-[var(--gray1)]">{CF[x.cf]}</span>}
          </span>
          <span className="flex justify-center">
            {editable ? <Switch checked={x.on} onChange={(v) => patch(i, "on", v)} /> : <span className="text-[11.5px] text-[var(--gray2)]">{x.on ? "On" : "Off"}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function FormatPicker({ draft, more, setMore, onPick }) {
  const rec = structGroupFor(draft), cur = STRUCT[draft.struct][2];
  const show = SGROUPS.filter(([g]) => g === rec || g === cur || more);
  return (
    <div>
      {show.map(([g, l]) => (
        <div key={g} className="mb-3">
          <div className="text-[11px] font-bold text-[var(--gray2)] mb-1.5">{l}{g === rec ? <span className="text-[var(--gray3)]"> · suggested for this visit type</span> : null}</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(STRUCT).filter(([, v]) => v[2] === g).map(([k, [t, sub]]) => {
              const on = draft.struct === k;
              return (
                <button key={k} type="button" onClick={() => onPick(k)} data-testid={`vt-fmt-${k}`}
                  className="text-left rounded-[11px] border p-2.5 transition bg-white"
                  style={on ? { borderColor: IND, boxShadow: "0 0 0 3px rgba(88,86,214,.14)" } : { borderColor: "#E9E9EC" }}>
                  <b className="block text-[12.5px] text-[var(--ink)]">{t}</b>
                  <span className="block text-[10.5px] text-[var(--gray2)] mt-0.5">{sub}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => setMore(!more)} className="text-[12px] font-semibold" style={{ color: IND }}>{more ? "Fewer formats" : "More formats"}</button>
    </div>
  );
}

const TABS = [["basics", "Basics"], ["booking", "Booking"], ["note", "Note"], ["billing", "Billing & coding"], ["programs", "Programs & workflow"], ["advanced", "Advanced"]];

export function VisitTypeEditor({ vtId, onBack, onOpenDq }) {
  const { vts } = useVisitTypes();
  const stored = vts.find((v) => v.id === vtId);
  const [draft, setDraft] = useState(() => clone(stored));
  const [dirty, setDirty] = useState(!stored?.name);
  const [tab, setTab] = useState("basics");
  const [more, setMore] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [lens, setLens] = useState(null);
  const [leaveSheet, setLeaveSheet] = useState(false);

  const d = draft;
  const up = (patch) => { setDraft((p) => ({ ...p, ...patch })); setDirty(true); };
  const emOn = !!d.panels["E/M code"];
  const onCount = d.secs.filter((x) => x.on).length;
  const examApplies = d.secs.some((x) => x.on && x.slot === "exam");
  const emFamily = /^(E\/M|Preventive|Medicare|Consult|TCM)/.test(d.emf);
  const warnAP = d.note && d.billable && emFamily && !(d.secs.some((x) => x.on && x.slot === "assessment") && d.secs.some((x) => x.on && x.slot === "plan"));
  const warnTime = d.emf.startsWith("Psychotherapy") && !d.secs.some((x) => x.on && x.slot === "time");

  const doSave = () => { saveVisitType(draft); setDirty(false); };
  const doDiscard = () => { setDraft(clone(stored)); setDirty(false); };
  const doBack = () => { if (dirty && stored?.name) { setLeaveSheet(true); return; } if (!stored?.name && !draft.name) removeVisitType(draft.id); onBack(); };

  return (
    <div className="fade-up" data-testid="vt-editor">
      <button onClick={doBack} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] mb-3"><ChevronLeft size={14} /> Visit types</button>

      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-[18px] font-bold text-[var(--ink)]">{d.name || "New visit type"}</h2>
            {d.abbr ? <span className="text-[11px] font-semibold px-2 py-[2px] rounded-full border border-[#E9E9EC] text-[var(--gray2)]">{d.abbr}</span> : null}
          </div>
          <p className="text-[12px] text-[var(--gray2)] mt-1">{d.cat} · {d.dur ? d.dur + " min" : "no duration"} · {d.note ? STRUCT[d.struct][0] : "no note"}</p>
        </div>
        <div className="flex items-center gap-2 flex-none" data-testid="vt-save-model">
          {dirty
            ? <><span className="text-[11.5px] font-semibold" style={{ color: AMBER }}>Unsaved changes · apply to new notes</span>
                <button onClick={doDiscard} className="text-[12px] font-semibold px-3 py-1.5 rounded-[9px] border border-[#E9E9EC] text-[var(--ink)]" data-testid="vt-discard">Discard</button>
                <button onClick={doSave} className="text-[12px] font-bold px-3 py-1.5 rounded-[9px] text-white" style={{ background: "var(--ink)" }} data-testid="vt-save">Save</button></>
            : <span className="text-[11.5px] text-[var(--gray2)]">All changes saved</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-[10px] px-3 py-2 mb-3 text-[11.5px] font-semibold" style={{ background: "rgba(88,86,214,.06)", color: "#4A4AC2" }}>
        Needs API — the visit type API is in development; nothing here saves.<DqMarker n={60} onOpen={onOpenDq} />
      </div>

      <div className="flex gap-1 border-b border-[#E9E9EC] mb-4 overflow-x-auto" role="tablist">
        {TABS.map(([k, l]) => {
          const on = tab === k;
          return <button key={k} role="tab" aria-selected={on} onClick={() => setTab(k)} data-testid={`vt-tab-${k}`}
            className="text-[12.5px] font-semibold px-3 py-2 whitespace-nowrap border-b-2 -mb-px transition"
            style={{ color: on ? "var(--ink)" : "var(--gray2)", borderColor: on ? IND : "transparent" }}>{l}</button>;
        })}
      </div>

      {tab === "basics" && (
        <Fsec title="Basic information">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Visit type name"><Inp value={d.name} onChange={(v) => up({ name: v })} /></Field>
            <Field label="Abbreviation"><Inp value={d.abbr} onChange={(v) => up({ abbr: v })} /></Field>
            <Field label="Status"><Sel value={d.status} onChange={(v) => up({ status: v })} opts={["Enabled", "Disabled"]} /></Field>
            <Field label="Category"><Sel value={d.cat} onChange={(v) => up({ cat: v })} opts={CATEGORIES} /></Field>
          </div>
          <div className="mt-3">
            <span className="block text-[11px] font-semibold text-[var(--gray2)] mb-1">Default duration</span>
            <div className="flex items-center gap-1.5 flex-wrap">
              {[15, 20, 30, 45, 60].map((m) => <button key={m} type="button" onClick={() => up({ dur: m })} aria-pressed={d.dur === m}
                className="text-[12px] font-semibold px-2.5 py-1.5 rounded-[8px] border transition" style={d.dur === m ? { borderColor: IND, color: IND, background: "rgba(88,86,214,.08)" } : { borderColor: "#E9E9EC", color: "var(--ink)" }}>{m}m</button>)}
              <input type="number" min={0} step={5} placeholder="Other" value={[15, 20, 30, 45, 60].includes(d.dur) ? "" : d.dur} onChange={(e) => up({ dur: Number(e.target.value) })} className="w-[80px] rounded-[8px] border border-black/[.14] px-2 py-1.5 text-[12px]" aria-label="Other duration" />
            </div>
          </div>
          <div className="mt-3">
            <span className="block text-[11px] font-semibold text-[var(--gray2)] mb-1">Schedule colour <span className="text-[var(--gray3)] font-normal">· calendar only</span></span>
            <div className="flex gap-1.5 flex-wrap">
              {COLORS.map((c) => <button key={c} type="button" onClick={() => up({ color: c })} aria-pressed={d.color === c} aria-label={`Colour ${c}`}
                className="w-6 h-6 rounded-full transition" style={{ background: c, outline: d.color === c ? `2px solid ${IND}` : "none", outlineOffset: 2 }} />)}
            </div>
          </div>
          <div className="mt-3"><Field label="Description"><Inp value={d.desc} onChange={(v) => up({ desc: v })} placeholder="Optional" /></Field></div>
        </Fsec>
      )}

      {tab === "booking" && (
        <Fsec title="Booking">
          <SwitchRow label="Show as a primary booking tile" sub="Appears in the primary tiles row of the booking lightbox." checked={d.tile} onChange={(v) => up({ tile: v })} />
          <SwitchRow label="Referring physician required" sub="Booking prompts for a referring physician before confirming." checked={d.refReq} onChange={(v) => up({ refReq: v })} />
          <SwitchRow label="Group visit" sub="Several patients can share one slot with this provider." checked={d.group} onChange={(v) => up({ group: v })} />
          <SwitchRow label="Online / self booking" sub="Patients can book this visit type themselves." checked={d.online} onChange={(v) => up({ online: v })} />
          <div className="grid grid-cols-2 gap-3 mt-3"><Field label="Visit mode"><Sel value={d.mode} onChange={(v) => up({ mode: v })} opts={["In person", "Telehealth", "Either"]} /></Field></div>
          <p className="text-[11px] text-[var(--gray2)] mt-2">Telehealth visits get a video link automatically.</p>
        </Fsec>
      )}

      {tab === "note" && (
        <>
          <Fsec title="Encounter">
            <SwitchRow label="Creates a clinical encounter" sub="Off for administrative, phone-only or non-medical types." checked={d.note} onChange={(v) => up({ note: v })} />
            {d.note ? <>
              <SwitchRow label="Selectable at New Encounter" checked={d.newEnc} onChange={(v) => up({ newEnc: v })} />
              <SwitchRow label="Use in pre-visit prep" sub="revQ's pre-visit summary knows the visit type." checked={d.prep} onChange={(v) => up({ prep: v })} />
              <div className="grid grid-cols-2 gap-3 mt-3"><Field label="Patient"><Sel value={d.pt} onChange={(v) => up({ pt: v })} opts={["New", "Established", "Either"]} /></Field></div>
            </> : null}
          </Fsec>
          {d.note && <>
            <Fsec title="Note structure">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-[11.5px] text-[var(--gray2)]">The default structure for this visit type. Providers can override it and revert any time.</p>
                <div className="flex items-center gap-3 flex-none">
                  <button type="button" onClick={() => setLens(STRUCT[d.struct][2] === "med" ? "consult" : "soap")} className="text-[12px] font-semibold" style={{ color: IND }}>Set up from a sample via Lens</button>
                  <DqMarker n={60} onOpen={onOpenDq} />
                  <button type="button" onClick={() => up({ secs: mkSecs(d.struct) })} className="text-[12px] font-semibold" style={{ color: IND }}>Reset sections to {STRUCT[d.struct][0]}</button>
                </div>
              </div>
              <FormatPicker draft={d} more={more} setMore={setMore} onPick={(k) => up({ struct: k, secs: mkSecs(k) })} />
              {lens ? (
                <div className="rounded-[12px] p-3 mt-3" style={{ background: "rgba(88,86,214,.06)", border: "1px solid rgba(88,86,214,.25)" }} data-testid="vt-lens-card">
                  <b className="text-[12.5px] text-[var(--ink)]">Lens read the sample: this looks like a {STRUCT[lens][0]}.</b>
                  <div className="text-[11.5px] text-[var(--gray2)] mt-0.5">Use its sections for this visit type? The provider's style still comes from their own template.</div>
                  <div className="flex gap-2 mt-2.5">
                    <button onClick={() => setLens(null)} className="text-[12px] font-semibold px-3 py-1.5 rounded-[9px] border border-[#E9E9EC]">Keep {STRUCT[d.struct][0]}</button>
                    <button onClick={() => { up({ struct: lens, secs: mkSecs(lens) }); setLens(null); }} className="text-[12px] font-bold px-3 py-1.5 rounded-[9px] text-white" style={{ background: "var(--ink)" }}>Use {STRUCT[lens][0]}</button>
                  </div>
                </div>
              ) : null}
              <div className="flex items-center justify-between mt-4 mb-2">
                <span className="text-[11px] text-[var(--gray2)]">{onCount} of {d.secs.length} sections on · exam defaults {examApplies ? "apply" : "don’t apply (no exam section)"}</span>
                <button type="button" onClick={() => setShowMap(!showMap)} className="text-[12px] font-semibold" style={{ color: IND }} data-testid="vt-toggle-map">{showMap ? "Hide" : "Show"} section mapping</button>
              </div>
              <SectionRows list={d.secs} editable showMap={showMap} onChange={(next) => up({ secs: next })} />
              {d.struct === "narr" ? <p className="text-[11px] text-[var(--gray2)] mt-2.5">Narrative notes have one free-text section, so copy-forward and exam defaults don’t apply.</p> : null}
              <p className="text-[11px] text-[var(--gray2)] mt-2.5 flex items-center gap-1.5">Copied text is marked in the note until the provider confirms it.<DqMarker n={61} onOpen={onOpenDq} /></p>
            </Fsec>
            <Fsec title="Encounter panels">
              <p className="text-[11px] text-[var(--gray2)] mb-1">Loaded in the provider's encounter view with this visit type.</p>
              {PANELS.map((pn) => <SwitchRow key={pn} label={pn} checked={!!d.panels[pn]} onChange={(v) => up({ panels: { ...d.panels, [pn]: v } })} />)}
            </Fsec>
            <Fsec title="Staff labels">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Physician label"><Inp value={d.labels[0]} onChange={(v) => up({ labels: [v, d.labels[1]] })} /></Field>
                <Field label="Medical assistant label"><Inp value={d.labels[1]} onChange={(v) => up({ labels: [d.labels[0], v] })} /></Field>
              </div>
            </Fsec>
          </>}
        </>
      )}

      {tab === "billing" && (
        <Fsec title="Billing & coding">
          <SwitchRow label="Billable" sub="Off: a non-billable warning on booking, and these visits never become claims in revQ." checked={d.billable} onChange={(v) => up({ billable: v })} />
          <SwitchRow label="E/M code required" sub={emOn ? "The provider must pick an E/M code before closing the encounter." : "Turn on the E/M code panel (Note tab) to require it."} checked={d.emReq && emOn} onChange={(v) => up({ emReq: v })} disabled={!emOn} />
          <SwitchRow label="Accumulated billing" sub="Combines charges into a rolling billing cycle." checked={d.accum} onChange={(v) => up({ accum: v })} />
          <SwitchRow label="Generate a CCD on completion" sub="Continuity of Care Document." checked={d.ccd} onChange={(v) => up({ ccd: v })} />
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Expected E/M family"><Sel value={d.emf} onChange={(v) => up({ emf: v })} opts={EMF} /></Field>
            <Field label="Default place of service"><Sel value={d.pos} onChange={(v) => up({ pos: v })} opts={POS_OPTS} /></Field>
            <Field label="Telehealth modifier"><Sel value={d.telemod} onChange={(v) => up({ telemod: v })} opts={["95", "GT", "None"]} /></Field>
          </div>
          {warnAP ? <p className="text-[11.5px] mt-3 font-medium" style={{ color: AMBER }}>{STRUCT[d.struct][0]} has no assessment or plan section turned on — E/M-billed notes usually need both.</p> : null}
          {warnTime ? <p className="text-[11.5px] mt-3 font-medium" style={{ color: AMBER }}>Psychotherapy codes are time-based — turn on a Session time section (Note tab).</p> : null}
        </Fsec>
      )}

      {tab === "programs" && (
        <>
          <Fsec title="Care type">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Care type"><Sel value={d.care} onChange={(v) => up({ care: v })} opts={["Standard", "Well child", "Wellness", "OB", "Hospital"]} /></Field>
              {(d.care === "Well child" || d.care === "Wellness") ? <Field label="Recurs every (months)"><Inp type="number" value={d.recur || ""} onChange={(v) => up({ recur: Number(v) })} /></Field> : null}
            </div>
            {d.care === "OB" ? <div className="mt-1"><SwitchRow label="First visit in an OB series" checked={d.obFirst} onChange={(v) => up({ obFirst: v })} /></div> : null}
          </Fsec>
          <Fsec title="Counts toward programs">
            {["RPM", "TCM", "MTM", "CCM", "Care coordinator"].map((pg) => (
              <SwitchRow key={pg} label={pg} checked={d.programs.includes(pg)} onChange={(v) => up({ programs: v ? [...d.programs, pg] : d.programs.filter((x) => x !== pg) })} />
            ))}
          </Fsec>
          <Fsec title="Workflow">
            <SwitchRow label="Create a lab-review task on completion" checked={d.lab} onChange={(v) => up({ lab: v })} />
            <SwitchRow label="Transfer of care — inpatient" checked={d.tocIn} onChange={(v) => up({ tocIn: v })} />
            <SwitchRow label="Transfer of care — ambulatory" checked={d.tocAmb} onChange={(v) => up({ tocAmb: v })} />
            <SwitchRow label="Screen for patient-supplied equipment" checked={d.fpe} onChange={(v) => up({ fpe: v })} />
          </Fsec>
        </>
      )}

      {tab === "advanced" && (
        <>
          <Fsec title="Import names">
            <p className="text-[11px] text-[var(--gray2)] mb-2">Values an EHR or schedule file may send for this visit type.</p>
            <Inp value={d.imports.join(", ")} onChange={(v) => up({ imports: v.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="e.g. FU, F/U, Est pt" />
          </Fsec>
          <Fsec title="Availability">
            <div className="grid grid-cols-2 gap-3"><Field label="Providers"><Sel value={d.providers} onChange={(v) => up({ providers: v })} opts={["All providers", "Jack Goldman, M.D. only", "Sudeep Dhillon, M.D. only"]} /></Field></div>
          </Fsec>
          <Fsec title="Regulatory">
            <p className="text-[11px] text-[var(--gray2)] mb-1 flex items-center gap-1.5">Shown for practices in states that report them.<DqMarker n={61} onOpen={onOpenDq} /></p>
            {Object.keys(d.reg).map((r) => <SwitchRow key={r} label={r} checked={d.reg[r]} onChange={(v) => up({ reg: { ...d.reg, [r]: v } })} />)}
          </Fsec>
          <Fsec title="Document images">
            <div className="grid grid-cols-3 gap-3">
              {["Images per line", "Image width (px)", "Image height (px)"].map((l, i) => <Field key={l} label={l}><Inp type="number" value={d.img[i]} onChange={(v) => { const img = [...d.img]; img[i] = Number(v); up({ img }); }} /></Field>)}
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3"><Field label="Profile"><Sel value={d.profile} onChange={(v) => up({ profile: v })} opts={["Normal"]} /></Field></div>
          </Fsec>
        </>
      )}

      {leaveSheet ? (
        <div className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center bg-black/30" onClick={() => setLeaveSheet(false)}>
          <div className="bg-white rounded-[18px] p-5 w-[min(420px,92vw)]" onClick={(e) => e.stopPropagation()} data-testid="vt-leave-sheet">
            <div className="text-[15px] font-bold text-[var(--ink)]">Discard unsaved changes?</div>
            <p className="text-[12.5px] text-[var(--gray2)] mt-1">This visit type has unsaved edits. They apply to new notes only after you save.</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setLeaveSheet(false)} className="text-[12.5px] font-semibold px-3.5 py-2 rounded-[9px] border border-[#E9E9EC]">Keep editing</button>
              <button onClick={() => { setDraft(clone(stored)); setDirty(false); setLeaveSheet(false); onBack(); }} className="text-[12.5px] font-bold px-3.5 py-2 rounded-[9px] text-white" style={{ background: "var(--ink)" }}>Discard</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
